import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Download, Plus, Search, Upload } from "lucide-react";
import ExportDialog from "@/components/exports/ExportDialog";
import { useCanExport } from "@/hooks/useCanExport";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useGetPropertiesQuery } from "@/store/rest_api";
import { PropertyFormDialog } from "@/components/pages/properties/PropertyFormDialog";
import ImportWizard from "@/components/imports/ImportWizard";

const PROPERTY_TYPE_LABELS: Record<string, string> = {
  SINGLE_FAMILY: "Single Family",
  MULTI_FAMILY: "Multi Family",
  CONDO: "Condo",
  TOWNHOUSE: "Townhouse",
  MOBILE_HOME: "Mobile Home",
  LAND: "Land",
  COMMERCIAL: "Commercial",
  INDUSTRIAL: "Industrial",
  MIXED_USE: "Mixed Use",
  OTHER: "Other",
};

const OWNER_TYPE_LABELS: Record<string, string> = {
  OWNER_OCCUPIED: "Owner Occupied",
  ABSENTEE: "Absentee",
  CORPORATE: "Corporate",
  TRUST: "Trust",
  UNKNOWN: "Unknown",
};

const formatCurrency = (value?: number) => {
  if (value == null) return "—";
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
};

export default function PropertiesPage() {
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(0);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showImportWizard, setShowImportWizard] = useState(false);
  const [exportOpen, setExportOpen] = useState(false);
  const { canExport } = useCanExport();
  const navigate = useNavigate();

  const { data, isLoading } = useGetPropertiesQuery({
    page,
    size: 20,
    q: search || undefined,
  });

  const properties = data?.items || [];

  return (
    <div className="flex flex-col h-full p-4 space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold tracking-tight">Properties</h1>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => navigate("/properties/listings")}>
            <Search className="h-4 w-4 mr-1" />
            Search Listings
          </Button>
          <Button variant="outline" onClick={() => setShowImportWizard(true)}>
            <Upload className="h-4 w-4 mr-1" />
            Import
          </Button>
          {canExport && (
            <Button variant="outline" onClick={() => setExportOpen(true)}>
              <Download className="h-4 w-4 mr-1" />
              Export
            </Button>
          )}
          <Button onClick={() => setShowCreateDialog(true)}>
            <Plus className="h-4 w-4 mr-1" />
            Add Property
          </Button>
        </div>
      </div>

      <div className="relative">
        <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
        <Input
          placeholder="Search by address, city, ZIP, owner..."
          value={search}
          onChange={(e) => { setSearch(e.target.value); setPage(0); }}
          className="pl-8"
        />
      </div>

      {isLoading ? (
        <div className="space-y-2">
          {[1, 2, 3, 4, 5].map((i) => (
            <Skeleton key={i} className="h-12 w-full" />
          ))}
        </div>
      ) : (
        <div className="rounded-md border flex-1 overflow-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Address</TableHead>
                <TableHead>Type</TableHead>
                <TableHead>Beds/Baths</TableHead>
                <TableHead>Sqft</TableHead>
                <TableHead>Market Value</TableHead>
                <TableHead>Equity</TableHead>
                <TableHead>Owner</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {properties.length === 0 && (
                <TableRow>
                  <TableCell colSpan={7} className="text-center text-muted-foreground py-8">
                    No properties found
                  </TableCell>
                </TableRow>
              )}
              {properties.map((p) => (
                <TableRow
                  key={p.id}
                  className="cursor-pointer"
                  onClick={() => navigate(`/properties/${p.id}`)}
                >
                  <TableCell>
                    <div>
                      <div className="font-medium">{p.address}</div>
                      {p.city && (
                        <div className="text-xs text-muted-foreground">
                          {p.city}{p.state ? `, ${p.state}` : ""} {p.zip || ""}
                        </div>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>
                    {p.propertyType ? (
                      <Badge variant="outline">
                        {PROPERTY_TYPE_LABELS[p.propertyType] || p.propertyType}
                      </Badge>
                    ) : "—"}
                  </TableCell>
                  <TableCell>
                    {p.beds != null || p.baths != null
                      ? `${p.beds ?? "—"} / ${p.baths ?? "—"}`
                      : "—"}
                  </TableCell>
                  <TableCell>
                    {p.sqft != null ? new Intl.NumberFormat("en-US").format(p.sqft) : "—"}
                  </TableCell>
                  <TableCell>{formatCurrency(p.marketValue)}</TableCell>
                  <TableCell>{formatCurrency(p.equityEstimate)}</TableCell>
                  <TableCell>
                    <div>
                      <div className="text-sm">{p.ownerName || "—"}</div>
                      {p.ownerType && p.ownerType !== "UNKNOWN" && (
                        <div className="text-xs text-muted-foreground">
                          {OWNER_TYPE_LABELS[p.ownerType]}
                        </div>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      {data && data.totalPages > 1 && (
        <div className="flex items-center justify-center gap-2">
          <Button
            variant="outline"
            size="sm"
            disabled={page === 0}
            onClick={() => setPage(page - 1)}
          >
            Previous
          </Button>
          <span className="text-sm text-muted-foreground">
            Page {page + 1} of {data.totalPages}
          </span>
          <Button
            variant="outline"
            size="sm"
            disabled={data.last}
            onClick={() => setPage(page + 1)}
          >
            Next
          </Button>
        </div>
      )}

      <PropertyFormDialog
        open={showCreateDialog}
        onOpenChange={setShowCreateDialog}
      />

      <ImportWizard
        open={showImportWizard}
        onClose={() => setShowImportWizard(false)}
        entityType="PROPERTY"
      />

      <ExportDialog
        open={exportOpen}
        onClose={() => setExportOpen(false)}
        title={search ? "Export matching properties" : "Export properties"}
        description="Property details, the owner mailing address, and up to two owners with their phones and emails. Works as a direct mail file."
        request={{ kind: "PROPERTIES", propertySearch: search || undefined }}
      />
    </div>
  );
}
